/**
 * Org Polymarket US Keys — the manager panel for the entity account's API key.
 *
 * Polymarket US keys are org-pooled like intl Polymarket (Josh 09-16): one
 * ENABLED key per org (relay 409 POLY_US_ONE_KEY_PER_ORG), every trader in the
 * org trades through it, attribution comes from the audit ledger. The row is
 * a plain Ed25519 Key ID + Secret Key — no signature type, no funder, no
 * allowance. The relay saves the key only after a signed balances read
 * succeeds from the server, so a wrong pair or a blocked egress never sits on
 * file. Renders nothing unless the relay lane probe says the venue is on.
 */
import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Ban, Eye, EyeOff, Loader2, Plus, RefreshCw, Trash2, KeyRound } from 'lucide-react';
import { apiFetch } from '../../stores/authStore';
import { usePolymarketUsLaneStore } from '../../stores/polymarketUsLaneStore';
import { ConfirmModal } from '../../components/atoms/Modal';
import {
  CredentialHealthLines,
  CredentialStateBadge,
  type CredentialLifecycle,
  getCredentialState,
  isCredentialEnabled,
} from '../../components/credential/CredentialLifecycle';

export interface PolymarketUsOrgCredential extends CredentialLifecycle {
  id: string;
  label: string;
  /** The venue Key ID (public). */
  accessKey: string;
  venue: string;
  createdAt: string;
}

interface Props {
  orgQueryParam?: string;
  embedded?: boolean;
  className?: string;
}

const ONE_KEY_COPY = 'This org already has an enabled Polymarket US key; disable it first.';

export function PolymarketUsOrgKeysPanel({ orgQueryParam = '', embedded = false, className }: Props) {
  const lane = usePolymarketUsLaneStore((s) => s.status);
  const [creds, setCreds] = useState<PolymarketUsOrgCredential[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [label, setLabel] = useState('');
  const [keyId, setKeyId] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [hardDeleteTarget, setHardDeleteTarget] = useState<PolymarketUsOrgCredential | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);

  const enabledCount = creds.filter(isCredentialEnabled).length;

  const fetchCreds = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/manager/credentials${orgQueryParam}`);
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? 'Failed to load org keys');
        setCreds([]);
      } else {
        const all = (data.credentials ?? []) as PolymarketUsOrgCredential[];
        setCreds(all.filter((c) => c.venue === 'polymarket_us'));
      }
    } catch {
      setError('Network error');
    }
    setLoading(false);
  }, [orgQueryParam]);

  useEffect(() => {
    if (lane !== 'on') return;
    fetchCreds();
  }, [fetchCreds, lane]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim() || !keyId.trim() || !secretKey.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/manager/credentials${orgQueryParam}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          venue: 'polymarket_us',
          label: label.trim(),
          keyId: keyId.trim(),
          secretKey: secretKey.trim(),
        }),
      });
      const data = await res.json().catch(() => ({})) as {
        credential?: PolymarketUsOrgCredential;
        error?: string;
        code?: string;
      };
      if (!res.ok) {
        setError(data.code === 'POLY_US_ONE_KEY_PER_ORG' ? ONE_KEY_COPY : data.error ?? 'Failed to add key');
      } else if (data.credential) {
        setCreds((prev) => [...prev, data.credential as PolymarketUsOrgCredential]);
        setLabel('');
        setKeyId('');
        setSecretKey('');
        setShowForm(false);
      }
    } catch {
      setError('Network error');
    }
    setSubmitting(false);
  };

  const handlePatchCredential = useCallback(async (
    credential: PolymarketUsOrgCredential,
    body: { disabled?: boolean; release?: boolean },
  ) => {
    setActingId(credential.id);
    setError(null);
    const res = await apiFetch(`/api/manager/credentials/${credential.id}${orgQueryParam}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({})) as {
      credential?: PolymarketUsOrgCredential;
      error?: string;
      code?: string;
    };
    if (res.ok) {
      setCreds((prev) => prev.map((c) => (c.id === credential.id ? data.credential ?? c : c)));
    } else {
      setError(data.code === 'POLY_US_ONE_KEY_PER_ORG' ? ONE_KEY_COPY : data.error ?? 'Failed to update key');
    }
    setActingId(null);
  }, [orgQueryParam]);

  const handleHardDelete = async () => {
    if (!hardDeleteTarget) return;
    const id = hardDeleteTarget.id;
    setHardDeleteTarget(null);
    setActingId(id);
    const params = new URLSearchParams(orgQueryParam ? orgQueryParam.slice(1) : '');
    params.set('hard', 'true');
    params.set('confirm', 'delete');
    const res = await apiFetch(`/api/manager/credentials/${id}?${params.toString()}`, { method: 'DELETE' });
    if (res.ok) {
      setCreds((prev) => prev.filter((c) => c.id !== id));
    } else {
      const data = await res.json().catch(() => ({})) as { error?: string };
      setError(data.error ?? 'Failed to delete key');
    }
    setActingId(null);
  };

  if (lane !== 'on') return null;

  const Wrapper = embedded ? 'div' : 'section';
  const wrapperClassName = embedded
    ? `min-w-0 ${className ?? ''}`
    : `bg-card border-border rounded-lg border p-4 ${className ?? 'mb-6'}`;

  return (
    <Wrapper className={wrapperClassName} data-testid="polymarket-us-org-keys-panel">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5">
          <div className="flex shrink-0 items-center gap-2">
            <KeyRound size={16} className="text-muted-foreground" />
            <h3 className="text-sm font-semibold">Org Polymarket US Key</h3>
            <span className="text-muted-foreground text-[11px]">
              ({creds.length === 0 ? 'none' : `${enabledCount}/${creds.length} enabled`})
            </span>
          </div>
          <div
            className="flex items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[10px] leading-snug text-emerald-200/90 light:text-emerald-800"
            title="One enabled Polymarket US key per org. Every trader in the org trades the entity account through it; per-trader attribution comes from the order audit ledger."
          >
            <AlertTriangle size={12} className="shrink-0 text-emerald-100 light:text-emerald-800" />
            <span>
              <span className="font-medium text-emerald-100 light:text-emerald-800">Entity account:</span> one enabled key
              per org — the venue&apos;s Ed25519 Key ID + Secret from polymarket.us/developer.
            </span>
          </div>
        </div>
        <button onClick={() => setShowForm(!showForm)}
          className="flex shrink-0 items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500">
          <Plus size={14} /> {showForm ? 'Cancel' : 'Add Key'}
        </button>
      </div>

      {error && (
        <div className="mb-3 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</div>
      )}

      {showForm && (
        <form onSubmit={handleCreate} className="bg-muted/50 border-border mb-3 space-y-2 rounded-md border p-3">
          <div>
            <label className="text-muted-foreground mb-1 block text-[11px]">Label</label>
            <input value={label} onChange={(e) => setLabel(e.target.value)}
              className="bg-card border-border text-foreground w-full rounded-md border px-2 py-1 text-xs focus:border-blue-500 focus:outline-none"
              placeholder="e.g. entity account" />
          </div>
          <div>
            <label className="text-muted-foreground mb-1 block text-[11px]">Key ID</label>
            <input value={keyId} onChange={(e) => setKeyId(e.target.value)}
              className="bg-card border-border text-foreground w-full rounded-md border px-2 py-1 font-mono text-xs focus:border-blue-500 focus:outline-none"
              placeholder="uuid from polymarket.us/developer" />
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-muted-foreground text-[11px]">Secret Key</label>
              <button type="button" onClick={() => setShowSecret((v) => !v)} className="text-muted-foreground hover:text-foreground" aria-label={showSecret ? 'Hide secret' : 'Show secret'}>
                {showSecret ? <EyeOff size={13} /> : <Eye size={13} />}
              </button>
            </div>
            <input type={showSecret ? 'text' : 'password'} value={secretKey} onChange={(e) => setSecretKey(e.target.value)}
              autoComplete="off"
              className="bg-card border-border text-foreground w-full rounded-md border px-2 py-1 font-mono text-xs focus:border-blue-500 focus:outline-none"
              placeholder="shown once when the key is created" />
            <p className="text-muted-foreground mt-1 text-[10px]">
              Saved only after a signed balances read succeeds from this server — a 401 means a wrong
              pair; a network or 403 failure means the venue does not accept this server&apos;s egress.
              Encrypted at rest and never shown again.
            </p>
          </div>
          <button type="submit" disabled={submitting}
            className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50">
            {submitting && <Loader2 size={12} className="animate-spin" />} Save Key
          </button>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
        </div>
      ) : creds.length === 0 ? (
        <p className="text-muted-foreground py-2 text-center text-xs">
          No Polymarket US key yet. Add the entity account&apos;s key to enable the venue for this org.
        </p>
      ) : (
        <div className="space-y-2">
          {creds.map((c) => {
            const state = getCredentialState(c);
            const disabled = state.kind === 'disabled';
            const canRelease =
              !disabled &&
              (!!c.quarantinedUntil || Number(c.consecutiveAuthFailures ?? 0) > 0 || !!c.lastErrorAt);
            const busy = actingId === c.id;
            return (
              <div key={c.id} className={`border-border/60 bg-muted/30 rounded-md border px-3 py-2 ${disabled ? 'opacity-75' : ''}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="text-muted-foreground flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
                    <span className="text-foreground text-xs font-medium">{c.label}</span>
                    <CredentialStateBadge credential={c} />
                    <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] text-emerald-300 light:text-emerald-700">US</span>
                    <span className="text-muted-foreground/40">·</span>
                    <span className="font-mono">{c.accessKey.length > 12 ? `${c.accessKey.slice(0, 8)}…${c.accessKey.slice(-4)}` : c.accessKey}</span>
                    <span className="text-muted-foreground/40">·</span>
                    <span>Added {new Date(c.createdAt).toLocaleDateString()}</span>
                    <CredentialHealthLines credential={c} inline />
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
                    {canRelease && (
                      <button onClick={() => handlePatchCredential(c, { release: true })} disabled={busy}
                        title="Release quarantine and reset auth failure count"
                        className="text-muted-foreground inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] hover:bg-blue-500/10 hover:text-blue-400 disabled:opacity-50">
                        <RefreshCw size={12} className={busy ? 'animate-spin' : ''} /> Release
                      </button>
                    )}
                    <button onClick={() => handlePatchCredential(c, { disabled: !disabled })} disabled={busy}
                      title={disabled ? 'Enable key' : 'Disable key'}
                      className="text-muted-foreground inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] hover:bg-muted hover:text-foreground disabled:opacity-50">
                      <Ban size={12} /> {disabled ? 'Enable' : 'Disable'}
                    </button>
                    {disabled && (
                      <button onClick={() => setHardDeleteTarget(c)} disabled={busy} title="Permanently delete key"
                        className="text-muted-foreground inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] hover:bg-red-500/10 hover:text-red-400 disabled:opacity-50">
                        <Trash2 size={12} /> Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmModal
        isOpen={!!hardDeleteTarget}
        onClose={() => setHardDeleteTarget(null)}
        onConfirm={handleHardDelete}
        title="Permanently Delete Polymarket US Key"
        message={`Permanently delete "${hardDeleteTarget?.label}"? This removes the encrypted key material and lifecycle history. Disable is safer unless this key should never be recoverable.`}
        confirmLabel="Delete Permanently"
        variant="danger"
      />
    </Wrapper>
  );
}
